import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useClientCompany } from "../../components/client/useClientCompanyViewClient";
import {
  ClientBand,
  ClientHeader,
  ClientPage,
  StatTile,
} from "../../components/client/ClientUiViewClient";
import {
  ClientError,
  ClientMessage,
} from "../../components/client/ClientStatesViewClient";
import HomeSkeletonViewClient from "../../components/client/HomeSkeletonViewClient";
import MonthChartViewClient, {
  homeBounds,
} from "../../components/client/MonthChartViewClient";
import {
  DateRange,
  LIFETIME,
  resolveRange,
} from "../../components/client/DateRangePickerViewClient";
import CampaignsTableViewClient from "../../components/client/CampaignsTableViewClient";
import CompanyPickerViewClient from "../../components/client/CompanyPickerViewClient";
import { CampaignIcon } from "../../icons/customIcons";
import {
  allSubcampaigns,
  formatDate,
  formatMoney,
  formatNumber,
  toDailySeries,
} from "../../components/client/clientFormatViewClient";

/**
 * Inicio (de todos los roles): la gráfica de las campañas y, a su derecha, sus
 * tres cifras del mismo periodo que enseña la gráfica; debajo, las campañas de
 * la empresa elegida, tal como las ve el cliente.
 */
const HomeViewClient: React.FC = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  // Solo las campañas de la empresa elegida en el sidebar.
  const { data, campaigns, isLoading, isError, refetch } = useClientCompany();
  // Las fechas de la gráfica: las cifras de la derecha son de esas fechas.
  const [range, setRange] = useState<DateRange>(LIFETIME);
  const asOfDate = data?.asOfDate ?? "";

  const subs = useMemo(() => allSubcampaigns(campaigns), [campaigns]);

  // Las cifras del periodo, sumando cada día que cae dentro. La inversión, por
  // moneda: casi siempre una sola, pero no se suman monedas.
  const totals = useMemo(() => {
    const { from, to } = resolveRange(
      range,
      homeBounds(subs.map(({ sub }) => sub)),
      asOfDate
    );
    const inPeriod = (date: string) => date >= from && date <= to;
    let impressions = 0;
    let clicks = 0;
    const investment = new Map<string, number>();
    for (const { campaign, sub } of subs) {
      for (const point of sub.series)
        if (inPeriod(point.date)) {
          impressions += point.impressions ?? 0;
          clicks += point.clicks ?? 0;
        }
      const invested = toDailySeries(sub.series)
        .filter((point) => inPeriod(point.date))
        .reduce((sum, point) => sum + (point.investment ?? 0), 0);
      investment.set(
        campaign.currency,
        (investment.get(campaign.currency) ?? 0) + invested
      );
    }
    return { impressions, clicks, investment };
  }, [subs, range, asOfDate]);

  // Con una sola moneda, la gráfica también puede enseñar la inversión.
  const currencies = new Set(subs.map(({ campaign }) => campaign.currency));
  const singleCurrency = currencies.size === 1 ? [...currencies][0] : null;

  if (isLoading) return <HomeSkeletonViewClient />;
  if (isError || !data) return <ClientError onRetry={refetch} />;
  if (campaigns.length === 0)
    return (
      <ClientMessage
        title={t("clientView.empty.title")}
        message={t("clientView.empty.message")}
      >
        <CompanyPickerViewClient />
      </ClientMessage>
    );

  const investment =
    totals.investment.size === 0
      ? formatMoney(0, singleCurrency ?? "EUR", language)
      : [...totals.investment]
          .sort((a, b) => b[1] - a[1])
          .map(([currency, value]) => formatMoney(value, currency, language))
          .join(" · ");
  // Debajo de cada cifra, de cuándo es: el atajo elegido o las fechas.
  const shown = resolveRange(
    range,
    homeBounds(subs.map(({ sub }) => sub)),
    asOfDate
  );
  const periodHint =
    range.preset === "custom"
      ? `${formatDate(shown.from, language)} – ${formatDate(shown.to, language)}`
      : t(`clientView.range.${range.preset}`, { count: range.days });

  return (
    <ClientPage
      wide
      band={
        <ClientBand
          title={t("clientView.table.title")}
          icon={<CampaignIcon color="currentColor" size={22} />}
        >
          <CampaignsTableViewClient campaigns={campaigns} />
        </ClientBand>
      }
    >
      <ClientHeader>
        <CompanyPickerViewClient />
      </ClientHeader>

      {/* La gráfica a la izquierda, sin tarjeta, y las cifras en columna a su
          derecha; en pantallas estrechas las cifras van encima. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 1fr) 240px",
          },
          gap: { xs: 4, lg: 8 },
          alignItems: "center",
        }}
      >
        <Box sx={{ minWidth: 0, order: { xs: 2, lg: 1 } }}>
          <MonthChartViewClient
            campaigns={campaigns}
            currency={singleCurrency}
            range={range}
            onRangeChange={setRange}
            asOfDate={asOfDate}
            floating
          />
        </Box>
        <Box
          sx={{
            order: { xs: 1, lg: 2 },
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(3, minmax(0, 1fr))",
              lg: "minmax(0, 1fr)",
            },
            gap: { xs: 3, lg: 4 },
          }}
        >
          <StatTile
            floating
            label={t("clientView.stats.impressions")}
            value={formatNumber(totals.impressions, language)}
            hint={periodHint}
          />
          <StatTile
            floating
            label={t("clientView.stats.clicks")}
            value={formatNumber(totals.clicks, language)}
            hint={periodHint}
          />
          <StatTile
            floating
            label={t("clientView.stats.investment")}
            value={investment}
            hint={periodHint}
          />
        </Box>
      </Box>
    </ClientPage>
  );
};

export default HomeViewClient;
